import React from 'react';
import { Form, Input, Button, Row, Col, Typography } from 'antd';
import { User, Mail, Phone, Building } from 'lucide-react';

const { Title, Text } = Typography;

interface FeedbackInfoFormProps {
  initialData: {
    name: string;
    email: string;
    phone?: string;
    organization?: string;
  };
  onSubmit: (data: any) => void;
  onBack: () => void;
  showBackButton: boolean;
}

const FeedbackInfoForm: React.FC<FeedbackInfoFormProps> = ({
  initialData,
  onSubmit,
  onBack,
  showBackButton,
}) => {
  const [form] = Form.useForm();

  const handleSubmit = (values: any) => {
    onSubmit(values);
  };

  return (
    <div className="h-full flex flex-col max-w-2xl mx-auto">
      <div className="text-center mb-4">
        <Title level={3} className="text-gray-800 mb-1">
          Tell Us About Yourself
        </Title>
        <Text className="text-gray-600 text-base">
          Please provide your basic information to get started
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialData}
        requiredMark={false}
        className="flex-1 flex flex-col"
      >
        <div className="flex-1 space-y-3">
          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item
                name="name"
                label={
                  <span className="text-base font-medium flex items-center gap-2">
                    <User size={18} className="text-blue-500" />
                    Full Name
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter your full name' },
                  { min: 2, message: 'Name must be at least 2 characters' },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter your full name"
                  className="rounded-lg"
                  style={{ fontSize: '16px', padding: '10px 12px' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={24}>
              <Form.Item
                name="email"
                label={
                  <span className="text-base font-medium flex items-center gap-2">
                    <Mail size={18} className="text-blue-500" />
                    Email Address
                  </span>
                }
                rules={[
                  { required: true, message: 'Please enter your email address' },
                  { type: 'email', message: 'Please enter a valid email address' },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter your email address"
                  className="rounded-lg"
                  style={{ fontSize: '16px', padding: '10px 12px' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label={
                  <span className="text-base font-medium flex items-center gap-2">
                    <Phone size={18} className="text-gray-400" />
                    Phone <Text className="text-gray-400 text-xs">(Optional)</Text>
                  </span>
                }
              >
                <Input
                  size="large"
                  placeholder="Enter phone number"
                  className="rounded-lg"
                  style={{ fontSize: '16px', padding: '10px 12px' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="organization"
                label={
                  <span className="text-base font-medium flex items-center gap-2">
                    <Building size={18} className="text-gray-400" />
                    Organization <Text className="text-gray-400 text-xs">(Optional)</Text>
                  </span>
                }
              >
                <Input
                  size="large"
                  placeholder="Enter organization"
                  className="rounded-lg"
                  style={{ fontSize: '16px', padding: '10px 12px' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className="flex justify-between items-center pt-4 mt-auto">
          {showBackButton ? (
            <Button
              size="large"
              onClick={onBack}
              className="px-8 py-2 h-auto rounded-lg"
            >
              Back
            </Button>
          ) : (
            <div />
          )}

          <Button
            type="primary"
            size="large"
            htmlType="submit"
            className="px-12 py-2 h-auto rounded-lg bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700"
            style={{ fontSize: '16px' }}
          >
            Continue
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default FeedbackInfoForm;